// =============================================================================
// SDS — shared bits (mig 407, 30 Sep 2026)
// =============================================================================
// James, after the site incident and the Ventia HSE audits: "the SDS register
// is missing … the guys made a good point, can we add a add to SDS with the
// camera … then close this gap and add it to the site folder."
//
// The register itself (sds_register) has been in the schema since the
// baseline; it was empty because only a manager could add to it, from the
// desktop, one PDF at a time. Now:
//   • SdsList — the register as the crew sees it: this job's products first
//     (project_sds), then the whole company register with a search. Every
//     row opens its SDS in the phone's viewer. Pending rows show "awaiting
//     office check"; not_found rows show "sheet still to come".
//   • openSdsFile — signed download of the sheet (files_read RLS: 'general').
//   • GHS pictogram labels and the status pills, used by the site folder, the
//     phone register and the manager's Documents tab.
// Writing a row happens in SdsCaptureScreen (the camera flow) and in the
// Documents tab (approve / reject via app.sds_review).
// =============================================================================

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Camera, ExternalLink, FlaskConical, Search, ShieldAlert } from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { Pill, Spinner, type Tone } from "@/components/ui";
import { withReturn } from "@/lib/returnTo";

export type SdsStatus = "pending" | "approved" | "not_found" | "rejected";

export type SdsRow = {
  sds_id: string;
  product_slug: string;
  product_name: string;
  manufacturer: string | null;
  product_size: string | null;
  hazard_class: string | null;
  ghs_pictograms: string[] | null;
  sds_issue_date: string | null;
  sds_review_date: string | null;
  sds_file_id: string | null;
  status: SdsStatus;
  is_active: boolean;
  notes: string | null;
  source_url: string | null;
  search_note: string | null;
  added_by: string | null;
  added_from_project_id: string | null;
  label_photo_file_id: string | null;
  approved_at: string | null;
  created_at: string;
  file?: { r2_bucket: string; r2_object_key: string; file_name: string } | null;
};

export const SDS_SELECT = "sds_id, product_slug, product_name, manufacturer, product_size, hazard_class, ghs_pictograms, sds_issue_date, sds_review_date, sds_file_id, status, is_active, notes, source_url, search_note, added_by, added_from_project_id, label_photo_file_id, approved_at, created_at, file:files!sds_register_sds_file_id_fkey(r2_bucket, r2_object_key, file_name)";

// GHS pictogram enum (what the worker's verify tool returns) → the crew's words.
export const GHS_LABEL: Record<string, { label: string; tone: Tone }> = {
  flame:             { label: "Flammable",     tone: "critical" },
  flame_over_circle: { label: "Oxidiser",      tone: "critical" },
  exclamation:       { label: "Irritant",      tone: "warning" },
  corrosion:         { label: "Corrosive",     tone: "critical" },
  gas_cylinder:      { label: "Gas under pressure", tone: "warning" },
  skull:             { label: "Toxic",         tone: "critical" },
  health_hazard:     { label: "Health hazard", tone: "critical" },
  exploding_bomb:    { label: "Explosive",     tone: "critical" },
  environment:       { label: "Environment",   tone: "notice" },
};

export const SDS_STATUS: Record<SdsStatus, { label: string; tone: Tone }> = {
  approved:  { label: "SDS on file",           tone: "ok" },
  pending:   { label: "awaiting office check", tone: "warning" },
  not_found: { label: "sheet still to come",   tone: "warning" },
  rejected:  { label: "rejected",              tone: "critical" },
};

export function slugify(s: string): string {
  return String(s ?? "").toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80) || "product";
}

export function fmtDay(iso?: string | null) {
  return iso ? new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—";
}

// Open the sheet: a signed URL, then the phone's own PDF viewer (new tab —
// the same rule as the policy reader: an iframe on iOS is zoomed and scrolls
// sideways, the viewer is not).
export async function openSdsFile(file: { r2_bucket: string; r2_object_key: string } | null | undefined): Promise<void> {
  if (!file) { window.alert("No SDS file on this product yet."); return; }
  // Open the tab first so iOS treats it as a user gesture, then point it.
  const w = window.open("", "_blank");
  const res = await apiFetch("/sign-download", {
    method: "POST",
    body: JSON.stringify({ bucket: file.r2_bucket, object_key: file.r2_object_key, expires_in_seconds: 3600 }),
  });
  if (!res.ok) { w?.close(); window.alert("Couldn't open the SDS — try again."); return; }
  const { download_url } = await res.json();
  if (w) w.location.href = download_url; else window.open(download_url, "_blank");
}

export function GhsPills({ codes, small = true }: { codes?: string[] | null; small?: boolean }) {
  const list = (codes ?? []).filter(c => GHS_LABEL[c]);
  if (list.length === 0) return null;
  return <span className="inline-flex flex-wrap gap-1">{list.map(c => <Pill key={c} label={GHS_LABEL[c].label} tone={GHS_LABEL[c].tone} small={small}/>)}</span>;
}

export function useProjectSds(projectId: string | null | undefined) {
  return useQuery({
    queryKey: ["project_sds", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("project_sds")
        .select(`project_sds_id, created_at, note, sds:sds_register!project_sds_sds_id_fkey(${SDS_SELECT})`)
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as { project_sds_id: string; created_at: string; note: string | null; sds: SdsRow }[];
    },
  });
}

export function useSdsRegister(search: string) {
  return useQuery({
    queryKey: ["sds_register_list", search],
    queryFn: async () => {
      let q = (supabase as any).from("sds_register").select(SDS_SELECT).neq("status", "rejected").eq("is_active", true).order("product_name").limit(200);
      const s = search.trim();
      if (s) q = q.or(`product_name.ilike.%${s.replace(/[%,()]/g, " ")}%,manufacturer.ilike.%${s.replace(/[%,()]/g, " ")}%`);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as SdsRow[];
    },
  });
}

export function SdsRowCard({ row, sub }: { row: SdsRow; sub?: string }) {
  const st = SDS_STATUS[row.status] ?? SDS_STATUS.pending;
  const hasFile = !!row.file;
  return (
    <button type="button" onClick={() => hasFile ? openSdsFile(row.file) : undefined}
            className={`w-full text-left rounded-2xl border border-line bg-ink-raised p-3.5 flex items-start gap-3 ${hasFile ? "hover:border-accent/50" : "opacity-90"}`}>
      <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${row.status === "approved" ? "bg-accent/15 text-accent" : "bg-warn/15 text-warn"}`}>
        {row.status === "approved" ? <FlaskConical size={19}/> : <ShieldAlert size={19}/>}
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2 flex-wrap">
          <span className="text-[14px] font-semibold text-text">{row.product_name}</span>
          {row.status !== "approved" && <Pill label={st.label} tone={st.tone} small/>}
        </span>
        <span className="block text-[12px] text-text-muted">{[row.manufacturer, row.product_size].filter(Boolean).join(" · ") || "—"}{sub ? ` · ${sub}` : ""}</span>
        {row.hazard_class && <span className="block text-[11px] text-text-muted mt-0.5 truncate">{row.hazard_class}</span>}
        <span className="block mt-1"><GhsPills codes={row.ghs_pictograms}/></span>
      </span>
      {hasFile
        ? <span className="text-[11px] text-accent inline-flex items-center gap-1 shrink-0 self-center"><ExternalLink size={12}/> SDS</span>
        : <span className="text-[11px] text-text-muted shrink-0 self-center">no sheet</span>}
    </button>
  );
}

// The register as the crew sees it. With a projectId: this job's products
// first, with the camera button pre-set to that job. Without: the whole
// register with a search.
export function SdsList({ projectId, returnTo }: { projectId?: string | null; returnTo?: string | null }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const onJob = useProjectSds(projectId);
  const all = useSdsRegister(search);
  const onJobIds = new Set((onJob.data ?? []).map(x => x.sds?.sds_id));
  const rest = (all.data ?? []).filter(r => !onJobIds.has(r.sds_id));
  const addHref = withReturn(`/sds/add${projectId ? `?project=${projectId}` : ""}`, returnTo ?? (projectId ? `/projects/${projectId}` : "/sds"));

  return (
    <div className="space-y-3">
      <button type="button" onClick={() => navigate(addHref)}
              className="w-full rounded-2xl bg-accent text-black p-4 flex items-center gap-3 text-left">
        <span className="w-11 h-11 rounded-xl bg-black/15 flex items-center justify-center"><Camera size={22}/></span>
        <span className="flex-1"><span className="block text-[15px] font-bold">Add a product with the camera</span>
          <span className="block text-[12px] opacity-80">Photograph the label — Ops finds the SDS, you confirm it</span></span>
      </button>

      {projectId && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted px-1 mb-1.5">On this job</div>
          {onJob.isLoading ? <div className="py-4 flex justify-center"><Spinner size={16}/></div>
            : (onJob.data ?? []).length === 0
              ? <div className="rounded-2xl border border-dashed border-line p-4 text-[12px] text-text-muted">No products added on this job yet. Anything hazardous the crew uses here — aerosols, fuels, solvents, adhesives, paints — gets photographed and filed.</div>
              : <div className="space-y-2">{(onJob.data ?? []).filter(x => x.sds).map(x => <SdsRowCard key={x.project_sds_id} row={x.sds} sub={`added ${fmtDay(x.created_at)}`}/>)}</div>}
        </div>
      )}

      <div>
        <div className="text-[11px] uppercase tracking-wider text-text-muted px-1 mb-1.5">{projectId ? "The whole register" : "Company SDS register"}</div>
        <div className="relative mb-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"/>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search a product or brand"
                 className="w-full bg-ink-raised border border-line rounded-xl pl-9 pr-3 py-2.5 text-[14px] text-text placeholder:text-text-muted"/>
        </div>
        {all.isLoading ? <div className="py-4 flex justify-center"><Spinner size={16}/></div>
          : rest.length === 0
            ? <div className="rounded-2xl border border-dashed border-line p-4 text-[12px] text-text-muted">{search ? "No product matches." : "The register is empty — the first product goes in with the camera button above."}</div>
            : <div className="space-y-2">{rest.map(r => <SdsRowCard key={r.sds_id} row={r}/>)}</div>}
      </div>
    </div>
  );
}
